import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Box, Button, Card, CardContent, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Grid, TextField, Typography, Snackbar, Alert,
  Switch, FormControlLabel, Chip, IconButton, MenuItem, Select, InputLabel,
  FormControl, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Tooltip, Divider, Checkbox, ListItemText, OutlinedInput
} from "@mui/material";
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Star as StarIcon, StarBorder as StarBorderIcon,
  Payment as PaymentIcon, Refresh as RefreshIcon
} from "@mui/icons-material";
import { api_urls } from "../../utils/api-urls";

const GATEWAY_TYPES = [
  { value: "razorpay", label: "Razorpay", color: "#2196F3" },
  { value: "payu", label: "PayU", color: "#00B14F" },
  { value: "ccavenue", label: "CCAvenue", color: "#1A6FB8" },
  { value: "paytm", label: "Paytm", color: "#00BAF2" },
  { value: "phonepe", label: "PhonePe", color: "#5F259F" },
  { value: "cashfree", label: "Cashfree", color: "#00C853" },
  { value: "instamojo", label: "Instamojo", color: "#FF6B35" },
  { value: "easebuzz", label: "EaseBuzz", color: "#FF9800" },
  { value: "payumoney", label: "PayUMoney", color: "#E53935" },
  { value: "upigateway", label: "UPI Gateway", color: "#673AB7" },
];

const ALL_SERVICES = [
  "wallet", "flight", "hotel", "recharge", "puja", "yatra",
  "shopping", "subscription", "milansathi",
  "marketplace", "postbanao", "rojgar", "sanataniSadan",
  "vendorPlan", "jobs", "kundli", "fastag", "electricity", "gas", "dth"
];

const CREDENTIAL_FIELDS = {
  razorpay: [
    { key: "keyId", label: "Key ID", required: true },
    { key: "keySecret", label: "Key Secret", required: true },
  ],
  paytm: [
    { key: "merchantId", label: "Merchant ID", required: true },
    { key: "merchantKey", label: "Merchant Key", required: true },
    { key: "callbackUrl", label: "Callback URL" },
  ],
  phonepe: [
    { key: "merchantId", label: "Merchant ID", required: true },
    { key: "salt", label: "Salt Key", required: true },
    { key: "apiKey", label: "API Key" },
    { key: "callbackUrl", label: "Callback URL" },
  ],
  cashfree: [
    { key: "apiKey", label: "App ID", required: true },
    { key: "apiSecret", label: "Secret Key", required: true },
    { key: "callbackUrl", label: "Callback URL" },
  ],
  instamojo: [
    { key: "apiKey", label: "API Key", required: true },
    { key: "apiSecret", label: "Auth Token", required: true },
    { key: "salt", label: "Private Salt" },
  ],
  easebuzz: [
    { key: "merchantKey", label: "Merchant Key", required: true },
    { key: "salt", label: "Salt", required: true },
    { key: "callbackUrl", label: "Callback URL" },
  ],
  payumoney: [
    { key: "merchantKey", label: "Merchant Key", required: true },
    { key: "salt", label: "Salt", required: true },
    { key: "merchantId", label: "Merchant ID" },
  ],
  payu: [
    { key: "merchantKey", label: "Merchant Key", required: true },
    { key: "salt", label: "Salt", required: true },
    { key: "merchantId", label: "Merchant ID" },
  ],
  ccavenue: [
    { key: "merchantId", label: "Merchant ID", required: true },
    { key: "accessCode", label: "Access Code", required: true },
    { key: "workingKey", label: "Working Key", required: true },
  ],
  upigateway: [
    { key: "apiKey", label: "API Key", required: true },
  ],
};

const emptyForm = {
  name: "razorpay",
  displayName: "",
  isActive: true,
  isPrimary: false,
  credentials: {},
  mode: "live",
  enabledServices: [...ALL_SERVICES],
  settings: { minAmount: 1, maxAmount: 100000, currency: "INR", description: "Bharat Darshan Payment" },
};

const PaymentGateways = () => {
  const [gateways, setGateways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchGateways = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${api_urls}api/admin/payment-gateways`, { headers });
      setGateways(data.success ? data.gateways : []);
    } catch (err) {
      showSnack("Failed to fetch gateways", "error");
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchGateways(); }, [fetchGateways]);

  const showSnack = (message, severity = "success") => setSnack({ open: true, message, severity });

  const openAdd = () => {
    setEditId(null);
    setForm({ ...emptyForm, credentials: {}, settings: { ...emptyForm.settings } });
    setDialogOpen(true);
  };

  const openEdit = async (gw) => {
    try {
      // Fetch full details (with un-masked credentials)
      const { data } = await axios.get(`${api_urls}api/admin/payment-gateways/${gw._id}`, { headers });
      if (data.success) {
        const g = data.gateway;
        setForm({
          name: g.name,
          displayName: g.displayName || "",
          isActive: g.isActive,
          isPrimary: g.isPrimary,
          credentials: g.credentials || {},
          mode: g.mode || "live",
          enabledServices: g.enabledServices || [...ALL_SERVICES],
          settings: g.settings || { ...emptyForm.settings },
        });
        setEditId(g._id);
        setDialogOpen(true);
      }
    } catch (err) {
      showSnack("Failed to load gateway details", "error");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (editId) payload._id = editId;
      const { data } = await axios.post(`${api_urls}api/admin/payment-gateways`, payload, { headers });
      if (data.success) {
        showSnack(editId ? "Gateway updated" : "Gateway created");
        setDialogOpen(false);
        fetchGateways();
      } else {
        showSnack(data.message || "Failed", "error");
      }
    } catch (err) {
      showSnack(err.response?.data?.message || "Save failed", "error");
    }
    setSaving(false);
  };

  const handleToggle = async (gw) => {
    try {
      const { data } = await axios.put(`${api_urls}api/admin/payment-gateways/${gw._id}/toggle`, {}, { headers });
      if (data.success) {
        showSnack(`${gw.displayName || gw.name} ${data.gateway.isActive ? "activated" : "deactivated"}`);
        fetchGateways();
      }
    } catch (err) {
      showSnack("Toggle failed", "error");
    }
  };

  const handleSetPrimary = async (gw) => {
    try {
      const { data } = await axios.put(`${api_urls}api/admin/payment-gateways/${gw._id}/primary`, {}, { headers });
      if (data.success) {
        showSnack(`${gw.displayName || gw.name} set as primary`);
        fetchGateways();
      }
    } catch (err) {
      showSnack("Set primary failed", "error");
    }
  };

  const handleDelete = async () => {
    try {
      const { data } = await axios.delete(`${api_urls}api/admin/payment-gateways/${deleteConfirm._id}`, { headers });
      if (data.success) {
        showSnack("Gateway deleted");
        fetchGateways();
      } else {
        showSnack(data.message || "Delete failed", "error");
      }
    } catch (err) {
      showSnack(err.response?.data?.message || "Delete failed", "error");
    }
    setDeleteConfirm(null);
  };

  const getGatewayColor = (name) => GATEWAY_TYPES.find(g => g.value === name)?.color || "#999";

  const credFields = CREDENTIAL_FIELDS[form.name] || [];

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold">
          <PaymentIcon sx={{ mr: 1, verticalAlign: "middle" }} />
          Payment Gateways
        </Typography>
        <Box>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchGateways} sx={{ mr: 1 }}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
            Add Gateway
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={5}><CircularProgress /></Box>
      ) : gateways.length === 0 ? (
        <Card><CardContent><Typography color="text.secondary" align="center">No payment gateways configured. Add one to get started.</Typography></CardContent></Card>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell><b>Gateway</b></TableCell>
                <TableCell><b>Mode</b></TableCell>
                <TableCell><b>Status</b></TableCell>
                <TableCell><b>Primary</b></TableCell>
                <TableCell><b>Services</b></TableCell>
                <TableCell><b>Credentials</b></TableCell>
                <TableCell align="right"><b>Actions</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {gateways.map((gw) => (
                <TableRow key={gw._id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: getGatewayColor(gw.name) }} />
                      <Box>
                        <Typography fontWeight="bold">{gw.displayName || gw.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{gw.name}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={gw.mode?.toUpperCase() || "LIVE"} size="small"
                      color={gw.mode === "test" ? "warning" : "success"} variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip label={gw.isActive ? "Active" : "Inactive"} size="small"
                      color={gw.isActive ? "success" : "default"} />
                  </TableCell>
                  <TableCell>
                    {gw.isPrimary ? (
                      <Tooltip title="Primary Gateway"><StarIcon color="warning" /></Tooltip>
                    ) : (
                      <Tooltip title="Set as Primary">
                        <IconButton size="small" onClick={() => handleSetPrimary(gw)}>
                          <StarBorderIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {(gw.enabledServices || []).slice(0, 4).map(s => (
                        <Chip key={s} label={s} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                      ))}
                      {(gw.enabledServices || []).length > 4 && (
                        <Chip label={`+${gw.enabledServices.length - 4}`} size="small" color="primary" variant="outlined" sx={{ fontSize: 11 }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {gw.credentials?.keyId || gw.credentials?.merchantId || gw.credentials?.apiKey || "***"}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={gw.isActive ? "Deactivate" : "Activate"}>
                      <Switch checked={gw.isActive} onChange={() => handleToggle(gw)} size="small" />
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(gw)}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setDeleteConfirm(gw)}
                        disabled={gw.isPrimary}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editId ? "Edit Gateway" : "Add Gateway"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Gateway Type</InputLabel>
                <Select value={form.name} label="Gateway Type"
                  onChange={(e) => setForm({ ...form, name: e.target.value, credentials: {} })}
                  disabled={!!editId}>
                  {GATEWAY_TYPES.map(g => (
                    <MenuItem key={g.value} value={g.value}>{g.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Display Name" value={form.displayName}
                onChange={(e) => setForm({ ...form, displayName: e.target.value })}
                placeholder={GATEWAY_TYPES.find(g => g.value === form.name)?.label} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Mode</InputLabel>
                <Select value={form.mode} label="Mode"
                  onChange={(e) => setForm({ ...form, mode: e.target.value })}>
                  <MenuItem value="live">Live</MenuItem>
                  <MenuItem value="test">Test</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Switch checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />}
                label="Active" />
              <FormControlLabel
                control={<Switch checked={form.isPrimary} onChange={(e) => setForm({ ...form, isPrimary: e.target.checked })} color="warning" />}
                label="Primary" />
            </Grid>

            <Grid item xs={12}><Divider><Chip label="Credentials" /></Divider></Grid>

            {credFields.map(f => (
              <Grid item xs={12} sm={6} key={f.key}>
                <TextField fullWidth label={f.label} required={f.required}
                  value={form.credentials[f.key] || ""}
                  onChange={(e) => setForm({ ...form, credentials: { ...form.credentials, [f.key]: e.target.value } })}
                  type={f.key.toLowerCase().includes("secret") || f.key.toLowerCase().includes("key") ? "password" : "text"}
                  InputProps={{ autoComplete: "off" }} />
              </Grid>
            ))}

            <Grid item xs={12}><Divider><Chip label="Services" /></Divider></Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Enabled Services</InputLabel>
                <Select multiple value={form.enabledServices} label="Enabled Services"
                  input={<OutlinedInput label="Enabled Services" />}
                  renderValue={(selected) => (
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {selected.map(v => <Chip key={v} label={v} size="small" />)}
                    </Box>
                  )}>
                  {ALL_SERVICES.map(s => (
                    <MenuItem key={s} value={s}
                      onClick={() => {
                        const idx = form.enabledServices.indexOf(s);
                        const newServices = [...form.enabledServices];
                        if (idx > -1) newServices.splice(idx, 1);
                        else newServices.push(s);
                        setForm({ ...form, enabledServices: newServices });
                      }}>
                      <Checkbox checked={form.enabledServices.includes(s)} />
                      <ListItemText primary={s.charAt(0).toUpperCase() + s.slice(1)} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}><Divider><Chip label="Settings" /></Divider></Grid>

            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Min Amount" type="number"
                value={form.settings.minAmount}
                onChange={(e) => setForm({ ...form, settings: { ...form.settings, minAmount: Number(e.target.value) } })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Max Amount" type="number"
                value={form.settings.maxAmount}
                onChange={(e) => setForm({ ...form, settings: { ...form.settings, maxAmount: Number(e.target.value) } })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Currency" value={form.settings.currency}
                onChange={(e) => setForm({ ...form, settings: { ...form.settings, currency: e.target.value } })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Payment Description" value={form.settings.description}
                onChange={(e) => setForm({ ...form, settings: { ...form.settings, description: e.target.value } })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : editId ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete Gateway</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete <b>{deleteConfirm?.displayName || deleteConfirm?.name}</b>?</Typography>
          <Typography variant="caption" color="error">This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default PaymentGateways;
